/**
 * @file Sun/moon segmented control that flips the global theme.
 * @module @labcraft/ui/chrome/theme-toggle
 *
 * Rendered only inside {@link AccountMenu} — anonymous users have no
 * theme affordance and see whatever the `<html data-theme>` bootstrap
 * script chose (cookie if set; otherwise system).
 *
 * Persistence is a cookie at `.labcraft.dev` so the choice applies
 * across www / lab / admin without re-toggling per host. On dev hosts
 * (localhost / 127.0.0.1 / Vite ports) the Domain attribute is
 * omitted so the cookie still works origin-scoped.
 *
 * A `matchMedia` listener for `(prefers-color-scheme: dark)` only
 * takes effect when no cookie override is set — once the user
 * clicks, system changes are ignored.
 */

import { useEffect, useState, type JSX } from 'react'
import { MoonIcon, SunIcon } from '../icons/index.ts'
import {
  readCurrentTheme,
  readThemeCookie,
  setTheme as applyTheme,
  type Theme,
} from './theme.ts'
import styles from './theme_toggle.module.css'

export function ThemeToggle(): JSX.Element {
  const [theme, setThemeState] = useState<Theme>(() => readCurrentTheme())

  function setTheme(next: Theme): void {
    setThemeState(next)
    applyTheme(next)
  }

  // Track system theme changes only when the user hasn't opted in.
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (event: MediaQueryListEvent): void => {
      if (readThemeCookie()) return
      const next: Theme = event.matches ? 'dark' : 'light'
      setThemeState(next)
      document.documentElement.setAttribute('data-theme', next)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Re-read the cookie when the tab becomes visible so a theme flip
  // on a sibling tab (lab → www → admin) takes effect without a
  // refresh. The inline `index.html` bootstrap script does the same
  // for `<html data-theme>` — this listener additionally keeps the
  // ThemeToggle's React state in sync if the dropdown is open during
  // a tab switch. No-op when the cookie hasn't changed.
  useEffect(() => {
    function onVisibility(): void {
      if (document.visibilityState !== 'visible') return
      const cookie = readThemeCookie()
      if (!cookie) return
      if (cookie === theme) return
      setThemeState(cookie)
      document.documentElement.setAttribute('data-theme', cookie)
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [theme])

  return (
    <div className={styles.toggle} role="group" aria-label="Theme">
      <button
        type="button"
        className={styles.opt}
        aria-pressed={theme === 'light'}
        aria-label="Light theme"
        onClick={() => setTheme('light')}>
        <SunIcon />
      </button>
      <button
        type="button"
        className={styles.opt}
        aria-pressed={theme === 'dark'}
        aria-label="Dark theme"
        onClick={() => setTheme('dark')}>
        <MoonIcon />
      </button>
    </div>
  )
}
